// The origin whose `/_session-bridge` this page frames to borrow a session:
// the workspace shell for a canvas page, the canvas for a shell page, null
// for every other host. Moved out of crossAppAuth.js unchanged so the
// access-only re-ask (sessionBridgeReacquire.js) frames the same peer as the
// cold-boot handoff.
//
// Cookies with domain=.localhost are silently dropped by Chrome, so in dev the
// iframe bridge is the only cross-host path; on *.symbols.app the peer is the
// same-channel shell/canvas host (see sessionBridgePeers.js for the matching
// answer-side allow-list).

import { canvasOrigin, workspaceOrigin } from './canvasUrl.js'

export const sessionBridgePeerOrigin = () => {
  if (typeof window === 'undefined') return null
  const host = window.location.hostname
  const isCanvas = host.startsWith('canvas.')
  // (The `system-governance-intranet.` / `governance-intranet.` clauses are
  // gone, review D5: no such app is served, and the bridge allow-list
  // (sessionBridgePeers.js) never answered them.)
  const isWorkspace =
    host.startsWith('workspace.') ||
    // `us-at.symbols.app` is the retired workspace origin — full removal was
    // decided 2026-08-18. It still resolves today, so this clause must stay:
    // dropping it early breaks the cross-app auth bridge for anyone still on
    // that host. Once the host stops answering, this branch is dead code and
    // can go.
    host === 'us-at.symbols.app'
  if (host.endsWith('.localhost') || host === 'localhost') {
    // Scheme + port MUST come from the live location, not be hardcoded. The
    // portless proxy serves the whole *.localhost:1355 family over HTTPS, so a
    // hardcoded `http://` produced a DIFFERENT origin than the running page —
    // the iframe bridge then pointed at an origin the browser blocks as mixed
    // content and the handoff silently never completed.
    const scheme = window.location.protocol === 'https:' ? 'https:' : 'http:'
    const port = window.location.port ? `:${window.location.port}` : ''
    if (isCanvas) return `${scheme}//workspace.localhost${port}`
    if (isWorkspace) return `${scheme}//canvas.localhost${port}`
    return null
  }
  // Channel-mapped peer origin for prod-style hosts. The standalone canvas
  // hosts (`edit.symbols.app`, `canvas.<tier>.symbols.app`) are deleted
  // (2026-10-23/24, sessionBridgePeers.js), so on *.symbols.app the borrower
  // is the comm host below; the canvas clauses only still match the local
  // canvas dev server (`canvas.localhost`, handled above).
  // The comm host (`<tier>.comm.symbols.app`, commHost.js) borrows from the
  // workspace shell of its own tier — the `comm` label (or the legacy `com`)
  // → `my` — the moment it has no login of its own, so a person signed in on
  // my.symbols.app opens comm.symbols.app signed in. Its own sign-in marks the
  // session 'own' like the shell's, so it is NOT an isSessionBridgeBorrower
  // below (that flag means "never logs in itself").
  const commTwin = /^((?:[a-z0-9-]+\.)*)comm?\.symbols\.app$/.exec(String(host || '').toLowerCase())
  if (commTwin) return `https://${commTwin[1]}my.symbols.app`
  if (host.endsWith('.symbols.app')) {
    if (isCanvas) return workspaceOrigin()
    if (isWorkspace) return canvasOrigin()
    return null
  }
  return null
}

// The origin that BORROWS its session from the bridge by design: the canvas
// (today only the local canvas dev server, `canvas.localhost` — the standalone
// canvas hosts are deleted).
// Login is a shell concern; the canvas takes the shell's access token. Only
// here is a refresh token without the 'own' mark read as a copy an older
// bridge build handed over (review D1, sessionOwnership.js) — on the shell an
// unmarked refresh token is always the shell's own login.
//
// FA204 / frank trap — self-contained (the host test is repeated, not shared).
export const isSessionBridgeBorrower = () => {
  if (typeof window === 'undefined' || !window.location) return false
  const host = String(window.location.hostname || '')
  return host.startsWith('canvas.')
}
